import { useCallback, useEffect, useState } from "react";
import { Lock, LogOut, Plus } from "lucide-react";
import { toast } from "sonner";
import {
  listDecodes,
  createDecode,
  updateDecode,
  deleteDecode,
  checkAdmin,
  setAdminToken,
  getAdminToken,
  clearAdminToken,
} from "@/lib/api";
import DecodeEditor from "@/components/decodes/DecodeEditor";
import DecodeView from "@/components/decodes/DecodeView";
import DecodesGrid from "@/components/decodes/DecodesGrid";
import AdminLoginPanel from "@/components/decodes/AdminLoginPanel";

export default function Decodes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null); // currently-viewed decode
  const [admin, setAdmin] = useState(!!getAdminToken());
  const [showLogin, setShowLogin] = useState(false);
  const [editing, setEditing] = useState(null); // decode object or {new:true}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listDecodes({ limit: 100 });
      setItems(r.items || []);
    } catch {
      toast.error("Failed to load decodes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const logIn = async (token) => {
    try {
      await checkAdmin(token);
      setAdminToken(token);
      setAdmin(true);
      setShowLogin(false);
      toast.success("Admin mode unlocked");
    } catch {
      toast.error("Invalid token");
    }
  };

  const logOut = () => {
    clearAdminToken();
    setAdmin(false);
    setEditing(null);
    toast.info("Logged out");
  };

  const save = async (draft) => {
    try {
      if (draft.id) {
        await updateDecode(draft.id, draft);
        toast.success("Decode updated");
      } else {
        await createDecode(draft);
        toast.success("Decode published");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed — check admin token");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this decode? This cannot be undone.")) return;
    try {
      await deleteDecode(id);
      toast.success("Decode deleted");
      setOpen(null);
      await load();
    } catch {
      toast.error("Delete failed");
    }
  };

  // ---- single-decode view ----
  if (open) {
    return (
      <>
        <DecodeView
          decode={open}
          admin={admin}
          onBack={() => setOpen(null)}
          onEdit={() => setEditing({ ...open })}
          onDelete={() => remove(open.id)}
        />
        {editing && <DecodeEditor draft={editing} onClose={() => setEditing(null)} onSave={save} />}
      </>
    );
  }

  // ---- index view ----
  return (
    <section className="space-y-8">
      {/* Header row */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] muted mb-2">// Decodes</div>
          <h1 className="font-display text-3xl md:text-5xl tracking-tight">
            From the <span className="gold italic">decoder's desk</span>
          </h1>
          <p className="muted mt-2 text-sm max-w-2xl">
            Public decodes — neutral lens, all sides honoured. Linked to the dictionary for instant cross-reference.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {admin ? (
            <>
              <button
                onClick={() => setEditing({ new: true, title: "", body: "", tags: [], author: "Gem Key" })}
                className="btn-primary px-4 py-2 text-xs flex items-center gap-2"
                data-testid="decode-new"
              >
                <Plus size={14} /> New Decode
              </button>
              <button
                onClick={logOut}
                className="btn-secondary px-3 py-2 text-[11px] flex items-center gap-1.5"
                data-testid="decode-logout"
                title="Log out of admin"
              >
                <LogOut size={12} />
              </button>
            </>
          ) : (
            <button
              onClick={() => setShowLogin(true)}
              className="btn-secondary px-3 py-2 text-[11px] flex items-center gap-1.5"
              data-testid="decode-login"
            >
              <Lock size={12} /> Admin
            </button>
          )}
        </div>
      </div>

      {showLogin && (
        <AdminLoginPanel onSubmit={logIn} onCancel={() => setShowLogin(false)} />
      )}

      <DecodesGrid loading={loading} items={items} admin={admin} onOpen={setOpen} />

      {editing && <DecodeEditor draft={editing} onClose={() => setEditing(null)} onSave={save} />}
    </section>
  );
}
